import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { distinctUntilKeyChanged, takeUntil } from 'rxjs/operators';

// Store
import { Claim, claimQuery, fromClaimActions } from '@amfam/claim/data-access';
import { Adjuster, AdjusterListService } from '@amfam/claim/shared/adjuster-list';
import { env } from '@amfam/environment';
import { AnalyticsFacade, PageAnalyticParams } from '@amfam/shared/analytics';
import { Party } from '@amfam/shared/models';
import { QuickLink } from '@amfam/shared/ui/quick-links';
import { userQuery, UserState } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../core/store';
import { ContactAdjusterInternalEmail } from './contact-adjuster-internalemail.model';
import { ContactAdjuster } from './contact-adjuster.model';
import { ContactAdjusterService } from './contact-adjuster.service';

@Component({
  selector: 'app-contact-adjuster',
  templateUrl: './contact-adjuster.component.html',
  styleUrls: ['./contact-adjuster.component.scss'],
  standalone: false
})
export class ContactAdjusterComponent implements OnInit, OnDestroy {
  public party: Party;
  public contactInfo: ContactAdjuster;
  public submitted = false;
  public customer: UserState;
  isContactBillingToggleEnabled = this.featureFlagService.isEnabled('contact_billing');
  componentData: any;
  public quickLinksList = new Array<QuickLink>();

  private claimId: string;
  private adjuster: Adjuster;
  private claim: Claim = new Claim();
  private isConnectPartner: boolean;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  // **** Start of Analytics data for this component
  private pageAnalytic: PageAnalyticParams = {
    pageName: 'MyAccount:Claims:ContactAdjuster',
    experience: '',
    primaryCategory: 'My Account',
    subCategory1: 'Claims',
    subCategory2: '',
    subCategory3: ''
  };
  private sendAnalytic: PageAnalyticParams = {
    pageName: 'MyAccount:Claims:ContactAdjuster:Complete',
    experience: '',
    primaryCategory: 'My Account',
    subCategory1: 'Claims',
    subCategory2: '',
    subCategory3: ''
  };
  // **** End of Analytics data for this component

  constructor(
    private route: ActivatedRoute,
    public spinner: LoadingSpinnerService,
    private pageTitle: PageTitleService,
    private caService: ContactAdjusterService,
    private copyService: CopyService,
    private adjusterListService: AdjusterListService,
    private store: Store<fromRoot.RootState>,
    private analyticsFacade: AnalyticsFacade,
    private featureFlagService: FeatureFlagService
  ) {
    // Start the loading spinner
    this.spinner.start();

    // Subscribe to get the route params
    this.route.params.pipe(takeUntil(this.stop$)).subscribe(params => {
      const id = params['id'];
      this.claimId = id;
      this.store.dispatch(fromClaimActions.SetSelectedClaim(this.claimId));
    });

    this.store.select(userQuery.selectPartyDetails).subscribe(
      party => {
        this.party = party;
      },
      err => {
        this.analyticsFacade.trackPageAndError(this.pageAnalytic, 'ContactAdjusterLoadError');
        throw err;
      },
      () => {
        this.spinner.stop();
      }
    );

    combineLatest(
      this.store.select(claimQuery.selectSelectedClaim),
      this.store
        .select(userQuery.selectUserState)
        .pipe(
          distinctUntilKeyChanged(
            'customerId',
            (prev: string, next: string) => prev.toLowerCase() === next.toLowerCase()
          )
        ),
      this.store.select(BrandSelectors.selectIsPartner),
      (selectedClaim, userState, isPartner) => ({
        selectedClaim: selectedClaim,
        userState: userState,
        isPartner: isPartner
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(state => {
        if (state.selectedClaim) {
          this.claim = state.selectedClaim;
          if (
            this.claim.primaryFileHandler !== null &&
            this.claim.primaryFileHandler.name !== null &&
            this.claim.primaryFileHandler.emails.length > 0
          ) {
            this.adjuster = this.adjusterListService.getAdjuster(this.claim);
          }
          this.spinner.stop();
        }
        if (state.userState !== null && state.userState.customerId) {
          this.customer = state.userState;
        }
        // Check if user is non-AFI partner
        if (state.isPartner) {
          this.isConnectPartner = true;
        }
      });

    this.quickLinksList = [
      new QuickLink('Make a Payment', '/billing'),
      new QuickLink('Request a Change', '/policies/changerequest'),
      new QuickLink('Contact a Billing Specialist', '/billing/contact'),
      new QuickLink('FAQs', env.links.amfamDotComClaimsFaqUrl, 'amfam')
    ]
      .filter(
        link =>
          // filter Request a Change link if non-AFI partner
          !(
            (_get(link, 'text') === 'Request a Change' || _get(link, 'text') === 'FAQs') &&
            this.isConnectPartner
          )
      )
      .filter(
        link =>
          // fitler Contact a Billing Specialist link if contact billing is toggled off
          !(
            _get(link, 'text') === 'Contact a Billing Specialist' &&
            !this.isContactBillingToggleEnabled
          )
      );

    this.componentData = this.copyService.getComponentData('contactAdjuster');
  }

  ngOnInit() {
    this.pageTitle.set('Contact Your Claims Adjuster');
    this.analyticsFacade.trackPage(this.pageAnalytic);
  }

  handleContactInfo(contactInfo: ContactAdjuster) {
    const email = new ContactAdjusterInternalEmail();
    email.firstName = contactInfo.firstName;
    email.lastName = contactInfo.lastName;
    email.email = contactInfo.email;
    email.phone = contactInfo.phone;
    email.comment = contactInfo.comment;
    email.adjusterEmail = this.adjuster.emailUrl;
    email.adjusterName = this.adjuster.name;
    email.claimNumber = this.claimId;
    if (!env.production) {
      email.adjusterEmail = env.claimContantAdjusterInternal;
      email.adjusterName = 'DMSIMAILADJUSTER';
    }

    this.caService
      .generateInternalEmail(email)
      .pipe(takeUntil(this.stop$))
      .subscribe(results => {
        this.contactInfo = contactInfo;
        this.analyticsFacade.trackPage(this.sendAnalytic);
        this.submitted = true;
      });
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
